// Fondo Cripto → Bitácora — el playbook del FONDO COMÚN (Maxi + Nelson) que
// gestiona Plutus. Timeline de análisis con decisión, lecciones, hitos y
// rebalanceos, con los gráficos reales. Las entradas las escribe Plutus desde
// Telegram (registrar_en_fondo) o se crean a mano acá. Espacio SEPARADO del
// trading personal de Maxi (Njord): tabla y endpoint /fund/* propios.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/Textarea';
import { cn } from '../lib/cn';
import { FondoHeader } from '../components/fund/FondoHeader';
import {
  BIAS_TONE,
  FUND_DECISION_META,
  FUND_KIND_META,
  FundKindBadge,
  ScreenshotGrid,
  fmtFecha,
  type FundEntry,
} from '../components/fund/shared';

const PAGE = 20;

const KIND_FILTERS = [
  { value: '', label: 'Todo' },
  { value: 'analisis', label: 'Análisis' },
  { value: 'leccion', label: 'Lecciones' },
  { value: 'hito', label: 'Hitos' },
  { value: 'rebalanceo', label: 'Rebalanceos' },
] as const;

interface ListResponse {
  total: number;
  entries: FundEntry[];
}

export default function FondoBitacora(): ReactElement {
  const qc = useQueryClient();
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [pages, setPages] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FundEntry | null>(null);

  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (tag) params.set('tag', tag);
  if (q) params.set('q', q);
  params.set('limit', String(PAGE * pages));

  const { data, isLoading } = useQuery({
    queryKey: ['fund-journal', kind, tag, q, pages],
    queryFn: () => api<ListResponse>(`/fund/journal?${params.toString()}`),
  });

  const { data: tagsData } = useQuery({
    queryKey: ['fund-journal-tags'],
    queryFn: () => api<{ tags: Array<{ tag: string; count: number }> }>('/fund/journal/tags'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/fund/journal/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-journal'] });
      qc.invalidateQueries({ queryKey: ['fund-journal-tags'] });
      setDeleteTarget(null);
    },
  });

  const entries = data?.entries ?? [];
  const grupos = useMemo(() => {
    const by = new Map<string, FundEntry[]>();
    for (const e of entries) {
      const dia = new Intl.DateTimeFormat('es-CL', {
        timeZone: 'America/Santiago',
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date(e.createdAt));
      if (!by.has(dia)) by.set(dia, []);
      by.get(dia)!.push(e);
    }
    return [...by.entries()];
  }, [entries]);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <FondoHeader
          title="Bitácora del Fondo"
          subtitle="Análisis con decisión, lecciones, hitos y rebalanceos del fondo común — con los gráficos reales. Lo escribe Plutus en cada sesión; acá queda el playbook del fondo."
          action={
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              + Entrada manual
            </Button>
          }
        />

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => {
                setKind(f.value);
                setPages(1);
              }}
              className={cn(
                'px-3 h-8 rounded-full text-xs font-semibold border transition-colors',
                kind === f.value
                  ? 'bg-accent-500/15 text-accent-500 border-accent-500/50'
                  : 'bg-surface-panel text-ink-muted border-surface-border hover:border-surface-strong',
              )}
            >
              {f.label}
            </button>
          ))}
          <div className="flex-1 min-w-[160px] flex gap-2">
            <Input
              placeholder="Buscar en la bitácora del fondo…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setQ(qInput.trim());
                  setPages(1);
                }
              }}
            />
          </div>
          {(tagsData?.tags.length ?? 0) > 0 && (
            <Select
              value={tag}
              onChange={(v) => {
                setTag(v);
                setPages(1);
              }}
              options={[
                { value: '', label: 'Todos los tags' },
                ...(tagsData?.tags ?? []).map((t) => ({
                  value: t.tag,
                  label: `#${t.tag} (${t.count})`,
                })),
              ]}
            />
          )}
        </div>

        {isLoading ? (
          <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
        ) : entries.length === 0 ? (
          <EmptyState filtered={!!(kind || tag || q)} />
        ) : (
          <div className="space-y-10">
            {grupos.map(([dia, items]) => (
              <section key={dia}>
                <h2 className="text-xs font-semibold text-ink-subtle uppercase tracking-[0.18em] mb-4 first-letter:uppercase">
                  {dia}
                </h2>
                <div className="space-y-4 border-l-2 border-surface-border pl-5 ml-1">
                  {items.map((e) => (
                    <EntryCard
                      key={e.id}
                      entry={e}
                      onTagClick={(t) => {
                        setTag(t);
                        setPages(1);
                      }}
                      onDelete={() => setDeleteTarget(e)}
                    />
                  ))}
                </div>
              </section>
            ))}
            {entries.length < (data?.total ?? 0) && (
              <div className="text-center">
                <Button variant="secondary" onClick={() => setPages((p) => p + 1)}>
                  Cargar más ({entries.length} de {data?.total})
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} />}

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar entrada"
        description="Se elimina la entrada y sus pantallazos. No se puede deshacer."
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Eliminar
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">“{deleteTarget?.title}”</p>
      </Modal>
    </AppLayout>
  );
}

// ── Card de una entrada ──────────────────────────────────────────────────────
function EntryCard({
  entry,
  onTagClick,
  onDelete,
}: {
  entry: FundEntry;
  onTagClick: (tag: string) => void;
  onDelete: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState(false);
  const [outcomeDraft, setOutcomeDraft] = useState(entry.outcome ?? '');

  const outcomeMutation = useMutation({
    mutationFn: (outcome: string) =>
      api(`/fund/journal/${entry.id}`, { method: 'PATCH', body: { outcome: outcome || null } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-journal'] });
      setEditingOutcome(false);
    },
  });

  const long = entry.summary.length > 420;
  const visibleSummary = !long || expanded ? entry.summary : entry.summary.slice(0, 420) + '…';
  const meta = FUND_KIND_META[entry.kind] ?? FUND_KIND_META.analisis;
  const decision = entry.decision?.toLowerCase();

  return (
    <article className="relative bg-surface-elevated border border-surface-border rounded-xl p-5 hover:border-surface-strong transition-colors">
      <span
        className={cn('absolute -left-[27px] top-6 w-3 h-3 rounded-full ring-4 ring-surface-base', meta.dot)}
      />
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <FundKindBadge kind={entry.kind} />
          {entry.symbol && (
            <span className="px-2 py-0.5 rounded bg-surface-panel text-ink-muted text-[11px] font-mono font-semibold">
              {entry.symbol}
            </span>
          )}
          {entry.bias && (
            <span
              className={cn(
                'px-2.5 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider',
                BIAS_TONE[entry.bias.toLowerCase()] ?? BIAS_TONE.neutral,
              )}
            >
              {entry.bias}
            </span>
          )}
          {decision && FUND_DECISION_META[decision] && (
            <span
              className={cn(
                'px-2.5 py-0.5 rounded-full border bg-transparent text-[11px] font-semibold',
                FUND_DECISION_META[decision]!.tone,
              )}
            >
              {FUND_DECISION_META[decision]!.label}
            </span>
          )}
          {entry.tradeRef && (
            <span className="px-2 py-0.5 rounded bg-surface-panel text-ink-subtle text-[11px] font-mono">
              #{entry.tradeRef}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-ink-subtle font-mono">
          {entry.source === 'plutus' && <span title="Registrado por Plutus">🪙 Plutus</span>}
          <time>{fmtFecha(entry.createdAt)}</time>
          <button
            onClick={onDelete}
            className="text-ink-subtle hover:text-loss-DEFAULT transition-colors px-1"
            title="Eliminar entrada"
          >
            ✕
          </button>
        </div>
      </header>

      <h3 className="text-base font-bold mt-3">{entry.title}</h3>
      {entry.author && (
        <div className="text-[11px] text-ink-subtle mt-0.5">por {entry.author}</div>
      )}
      <p className="text-sm text-ink-muted mt-2 whitespace-pre-line leading-relaxed">
        {visibleSummary}
      </p>
      {long && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-accent-500 hover:text-accent-600 font-semibold mt-1"
        >
          {expanded ? 'Ver menos' : 'Ver completo'}
        </button>
      )}

      <ScreenshotGrid shots={entry.screenshots} />

      {entry.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {entry.tags.map((t) => (
            <button
              key={t}
              onClick={() => onTagClick(t)}
              className="px-2 py-0.5 rounded-full bg-surface-panel border border-surface-border text-[11px] text-ink-muted hover:text-accent-500 hover:border-accent-500/40 transition-colors"
            >
              #{t}
            </button>
          ))}
        </div>
      )}

      {/* Post-mortem editable */}
      <div className="mt-4 pt-3 border-t border-surface-border/60">
        {editingOutcome ? (
          <div className="space-y-2">
            <Textarea
              label="Post-mortem / resultado"
              rows={3}
              value={outcomeDraft}
              onChange={(e) => setOutcomeDraft(e.target.value)}
              placeholder="¿Qué terminó pasando? ¿Se validó la tesis?"
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setEditingOutcome(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={outcomeMutation.isPending}
                onClick={() => outcomeMutation.mutate(outcomeDraft.trim())}
              >
                Guardar
              </Button>
            </div>
          </div>
        ) : entry.outcome ? (
          <div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wider">
                Post-mortem
              </span>
              <button
                onClick={() => setEditingOutcome(true)}
                className="text-[11px] text-ink-subtle hover:text-accent-500"
              >
                editar
              </button>
            </div>
            <p className="text-sm text-ink-muted mt-1 whitespace-pre-line">{entry.outcome}</p>
          </div>
        ) : (
          <button
            onClick={() => setEditingOutcome(true)}
            className="text-xs text-ink-subtle hover:text-accent-500 transition-colors"
          >
            + Agregar post-mortem (¿qué terminó pasando?)
          </button>
        )}
      </div>
    </article>
  );
}

// ── Crear entrada manual ─────────────────────────────────────────────────────
function CreateModal({ onClose }: { onClose: () => void }): ReactElement {
  const qc = useQueryClient();
  const [kind, setKind] = useState('analisis');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [symbol, setSymbol] = useState('');
  const [decision, setDecision] = useState('');
  const [author, setAuthor] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      api('/fund/journal', {
        method: 'POST',
        body: {
          kind,
          title: title.trim(),
          summary: summary.trim(),
          symbol: symbol.trim() || null,
          decision: decision || null,
          author: author.trim() || null,
          tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-journal'] });
      qc.invalidateQueries({ queryKey: ['fund-journal-tags'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Nueva entrada del fondo"
      description="Registro manual — Plutus registra solo desde Telegram, esto es para lo que quieras anotar tú."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={createMutation.isPending}
            disabled={title.trim().length < 3 || summary.trim().length < 10}
            onClick={() => createMutation.mutate()}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Tipo"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'analisis', label: 'Análisis' },
              { value: 'leccion', label: 'Lección' },
              { value: 'hito', label: 'Hito' },
              { value: 'rebalanceo', label: 'Rebalanceo' },
            ]}
          />
          <Select
            label="Decisión"
            value={decision}
            onChange={setDecision}
            options={[
              { value: '', label: '— (ninguna)' },
              { value: 'comprar', label: 'Comprar' },
              { value: 'vender', label: 'Vender' },
              { value: 'mantener', label: 'Mantener' },
              { value: 'observar', label: 'Observar' },
            ]}
          />
        </div>
        <Input label="Título" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Textarea
          label="Narrativa"
          rows={6}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="La tesis, por qué se decidió lo que se decidió, la conclusión…"
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Activo (opcional)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="BTC, ETH, RENDER…"
          />
          <Input
            label="Autor (opcional)"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Max, Nelson, equipo"
          />
        </div>
        <Input
          label="Tags (separados por coma)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="gestion-riesgo, dca, toma-ganancia"
        />
        {error && <p className="text-sm text-loss-DEFAULT">{error}</p>}
      </div>
    </Modal>
  );
}

function EmptyState({ filtered }: { filtered: boolean }): ReactElement {
  return (
    <div className="text-center py-16 border border-dashed border-surface-border rounded-xl">
      <div className="text-4xl mb-3">🪙</div>
      <h3 className="font-bold">
        {filtered ? 'Nada con esos filtros' : 'La bitácora del fondo está vacía'}
      </h3>
      <p className="text-sm text-ink-muted mt-1 max-w-md mx-auto">
        {filtered
          ? 'Prueba quitando filtros o buscando otra cosa.'
          : 'Plutus registra acá los análisis con decisión, las lecciones, los hitos y los rebalanceos del fondo. También puedes crear entradas a mano.'}
      </p>
    </div>
  );
}
